/**
 * Lo que comparten las páginas del paciente cuando algo falla (23/9/2026).
 *
 * Objetivo: que no haya «rotura de conexión» en los enlaces del paciente. Las
 * cuatro páginas (cuestionario de salud, mis pasos, confirmar cita y LOPD de
 * casa) tienen que decir lo mismo ante lo mismo, y cada una lo decía a su
 * manera: el cuestionario contaba un corte de red como enlace muerto, y los
 * avisos de enlace no válido no decían a qué número llamar. Aquí está UNA
 * vez lo que ha pasado de verdad y el teléfono al que llamar; cada página
 * pone sus palabras (tú o usted) encima.
 */
import { useEffect, useState } from 'react';
import { cargarClinica } from './clinica';

/*
 * NI UN TELÉFONO ESCRITO EN EL CÓDIGO (23/9/2026). Aquí había uno de
 * respaldo, el de una instalación concreta, para cuando no se podía leer el de
 * la clínica. Pero el mismo build sirve a varias instalaciones: a la paciente
 * de otra clínica se le decía que llamara a una que no es la suya. Ahora el
 * teléfono sale, por orden:
 *   1. de la respuesta del propio enlace o de Configuración
 *      (`/api/public/branding`);
 *   2. si ahora no llega ninguno (sin conexión, servidor caído), del último
 *      que ESTE dispositivo leyó bien de ESTA clínica: localStorage es de
 *      este origen, así que no puede traer el de otra;
 *   3. y si tampoco, ninguno: la página dice «llame a la clínica»
 *      (`aQuienLlamar`). Lo que no se sabe no se inventa.
 */

/** Dónde recuerda este dispositivo el último teléfono que leyó bien. */
const CLAVE_TELEFONO = 'clinicpilot-telefono-clinica';

/** El último teléfono leído en este dispositivo, o vacío. Con try/catch: en
 *  modo privado, con los datos del sitio bloqueados o en una vista web
 *  rara, localStorage puede lanzar, y eso no puede tirar la página. */
function telefonoRecordado() {
  try {
    return window.localStorage.getItem(CLAVE_TELEFONO) || '';
  } catch {
    return '';
  }
}

function recordarTelefono(tel) {
  try {
    if (tel) window.localStorage.setItem(CLAVE_TELEFONO, tel);
  } catch {
    /* sin almacenamiento: la próxima vez no habrá recordado, y ya está */
  }
}

/** «NNN NNN NNN»: nueve cifras en grupos de tres, se guarde como se guarde
 *  (sin el 34 delante). Si no son nueve cifras, tal cual. */
export function telefonoBonito(tel) {
  const texto = String(tel || '').trim();
  let digitos = texto.replace(/\D/g, '');
  if (digitos.length === 11 && digitos.startsWith('34')) digitos = digitos.slice(2);
  if (digitos.length === 9) return `${digitos.slice(0, 3)} ${digitos.slice(3, 6)} ${digitos.slice(6)}`;
  return texto;
}

/**
 * El teléfono que se enseña en una pantalla del paciente: el que mandó su
 * propio enlace (`delServidor`) si lo hay; si no, el de Configuración
 * (`/api/public/branding`, que solo se pide cuando hace falta: `pedir`); si
 * no llega ninguno, el último que este dispositivo leyó bien; y si tampoco,
 * VACÍO (23/9/2026). Quien lo pinte usa `aQuienLlamar`, que sin número dice
 * «llame a la clínica».
 */
export function useTelefonoClinica(delServidor = '', pedir = true) {
  const [deConfiguracion, setDeConfiguracion] = useState('');
  useEffect(() => {
    if (delServidor || !pedir) return undefined;
    let vivo = true;
    cargarClinica().then((d) => { if (vivo) setDeConfiguracion(d?.phone || ''); });
    return () => { vivo = false; };
  }, [delServidor, pedir]);
  const leido = telefonoBonito(delServidor || deConfiguracion);
  // Lo que se ha leído bien se recuerda para el día que no llegue nada.
  useEffect(() => { recordarTelefono(leido); }, [leido]);
  return leido || telefonoBonito(telefonoRecordado());
}

/**
 * A quién llamar, dicho entero: «llámanos al NNN NNN NNN» o, si no se sabe
 * el número, «llama a la clínica». Con `usted` para las páginas que hablan
 * de usted («llámenos al…», «llame a la clínica»). Así ninguna página deja
 * un «llámanos al .» a medias cuando no hay teléfono (23/9/2026).
 */
export function aQuienLlamar(telefono, { usted = false } = {}) {
  if (telefono) return usted ? `llámenos al ${telefono}` : `llámanos al ${telefono}`;
  return usted ? 'llame a la clínica' : 'llama a la clínica';
}

/** La misma frase al principio de una oración: «Llámenos al…». */
export function conMayuscula(texto) {
  return texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto;
}

/**
 * Qué ha pasado DE VERDAD al pedir el enlace:
 *   · 'invalido'   — 404 o 410: el enlace no existe o ya no vale. Es lo
 *                    ÚNICO que puede decir que el enlace no vale.
 *   · 'incompleto' — 422: el token no tiene la longitud que exige el
 *                    servidor (se cortó al copiarlo del mensaje).
 *   · 'espera'     — 429: el freno de peticiones por IP; dentro de un rato
 *                    entra.
 *   · 'conexion'   — sin respuesta (red cortada, timeout), 5xx o cualquier
 *                    otra cosa: no dice nada del enlace; se reintenta.
 */
export function tipoDeFallo(e) {
  const status = e?.response?.status;
  if (status === 404 || status === 410) return 'invalido';
  if (status === 422) return 'incompleto';
  if (status === 429) return 'espera';
  return 'conexion';
}

/** ¿Pudo guardarse aunque no sepamos la respuesta? Sin respuesta (la red se
 *  cortó a la vuelta, timeout) o un 5xx (el proxy pudo cortar una petición
 *  que el servidor sí terminó). Antes de decir «no se pudo», se pregunta. */
export function respuestaPerdida(e) {
  return !e?.response || e.response.status >= 500;
}

/**
 * Una foto que el servidor NO ha guardado (24/9/2026): 413, demasiado grande
 * (más de 52 megapíxeles, o más de 25 MB), o 503, el servidor está
 * preparando otras fotos. El texto del servidor ya va escrito para el
 * paciente; si no lo hay (el 413 lo cortó el proxy, o el 503 es de una
 * avería), uno de tú que dice qué hacer. Nunca un error suelto.
 */
export const FOTO_DEMASIADO_GRANDE = 'Tu foto es demasiado grande para subirla. Hazla con la cámara '
  + 'normal del móvil, o haz una captura de pantalla de la foto y sube esa.';
export const FOTO_SIN_CONFIRMAR = 'No hemos podido confirmar que la foto se haya subido. Comprueba tu '
  + 'conexión y vuelve a subirla (si ya había llegado, se sustituye).';

export function fotoRechazada(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  return e?.response?.status === 413 ? FOTO_DEMASIADO_GRANDE : FOTO_SIN_CONFIRMAR;
}

/**
 * Cuándo puede volver a intentarlo tras un 429. Si el servidor manda
 * Retry-After, esos minutos; si no, el tope de la ventana del freno
 * (`documents_router._PUBLIC_WINDOW_SEC`, una hora): el cubo es deslizante
 * y el hueco se abre, como mucho, al cumplirse la hora de la primera
 * petición contada.
 */
export function cuandoReintentar(e) {
  const cabeceras = e?.response?.headers || {};
  const segundos = parseInt(cabeceras['retry-after'] ?? cabeceras['Retry-After'], 10);
  if (Number.isFinite(segundos) && segundos > 0) {
    const minutos = Math.max(1, Math.ceil(segundos / 60));
    return `dentro de ${minutos} minuto${minutos === 1 ? '' : 's'}`;
  }
  return 'dentro de una hora como mucho';
}
